import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronRight, Download, FileText, FolderOpen, Package, Scale, Layers } from "lucide-react";
import { theme, card, countTag, emptyFrame } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, ApiError } from "../api/client";

// ── Types ─────────────────────────────────────────────────────────────────

interface EvidenceTreeDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
}

interface EvidenceTreeControl {
  controlId: string;
  controlName: string;
  kind: string;
  docs: EvidenceTreeDoc[];
}

interface EvidenceTreeDomain {
  domain: string | null;
  controls: EvidenceTreeControl[];
}

interface EvidenceTreeFramework {
  frameworkId: string;
  label: string;
  totalDocs: number;
  domains: EvidenceTreeDomain[];
}

interface EvidenceTree {
  orgName: string;
  frameworks: EvidenceTreeFramework[];
  legal: Array<{ regulation: string; docs: EvidenceTreeDoc[] }>;
  untagged: EvidenceTreeDoc[];
}

interface ExportResult {
  downloadUrl: string;
  fileCount: number;
  zipSizeBytes: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ── Tree leaf: a single document row ──────────────────────────────────────

function TreeDocRow({ doc }: { doc: EvidenceTreeDoc }) {
  const [viewState, setViewState] = useState<"idle" | "loading" | "error">("idle");

  async function handleView() {
    setViewState("loading");
    try {
      const { url } = await api.get<{ url: string }>(`/documents/${doc.id}/download-url`);
      window.open(url, "_blank", "noopener,noreferrer");
      setViewState("idle");
    } catch {
      setViewState("error");
      setTimeout(() => setViewState("idle"), 3000);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "7px 12px 7px 16px",
        borderRadius: 3,
        background: "var(--c-rowBg, transparent)",
        marginBottom: 3,
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <FileText size={13} style={{ flexShrink: 0, color: "var(--c-muted)" }} />
        <span style={{ fontSize: 12, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.fileName}
        </span>
        <span style={{ fontSize: 11, color: "var(--c-faint)", flexShrink: 0 }}>
          {fmtBytes(doc.sizeBytes)} · {fmtDate(doc.uploadedAt)}
        </span>
      </div>
      <button
        onClick={handleView}
        disabled={viewState === "loading"}
        style={{
          flexShrink: 0,
          padding: "3px 10px",
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 3,
          border: "1px solid var(--c-border)",
          background: "var(--c-cardBg)",
          color: viewState === "error" ? "var(--c-danger)" : "var(--c-accent)",
          cursor: viewState === "loading" ? "wait" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {viewState === "loading" ? "…" : viewState === "error" ? "Error" : "View"}
      </button>
    </div>
  );
}

// ── Control row (collapsible) ─────────────────────────────────────────────

function TreeControlRow({ control, defaultOpen = false }: { control: EvidenceTreeControl; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasFiles = control.docs.length > 0;

  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          width: "100%",
          padding: "6px 10px",
          borderRadius: 3,
          border: "none",
          background: open ? "var(--c-accentBg)" : "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={13} style={{ flexShrink: 0, color: "var(--c-muted)" }} /> : <ChevronRight size={13} style={{ flexShrink: 0, color: "var(--c-muted)" }} />}
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c-accent)", flexShrink: 0 }}>
          {control.controlId}
        </span>
        <span style={{ fontSize: 12, color: "var(--c-heading)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {control.controlName}
        </span>
        <span style={{ ...countTag, marginLeft: "auto", color: hasFiles ? "var(--c-heading)" : "var(--c-muted)" }}>
          {control.docs.length}
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 24, paddingTop: 2 }}>
          {hasFiles
            ? control.docs.map(d => <TreeDocRow key={d.id} doc={d} />)
            : <div style={{ fontSize: 11, color: "var(--c-faint)", padding: "4px 12px" }}>No evidence attached to this control</div>
          }
        </div>
      )}
    </div>
  );
}

// ── Domain section (collapsible) ──────────────────────────────────────────

function TreeDomainSection({ domain, controls }: { domain: string | null; controls: EvidenceTreeControl[] }) {
  const [open, setOpen] = useState(true);
  // Distinct files: one file tagged to several controls in the domain is one document (DOC-23).
  const totalDocs = new Set(controls.flatMap((c) => c.docs.map((d) => d.id))).size;

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "7px 10px",
          borderRadius: 3,
          border: "none",
          background: "var(--c-sectionBg, var(--c-bg))",
          cursor: "pointer",
          textAlign: "left",
          borderBottom: "1px solid var(--c-border)",
          marginBottom: 4,
        }}
      >
        {open ? <ChevronDown size={14} style={{ color: "var(--c-muted)" }} /> : <ChevronRight size={14} style={{ color: "var(--c-muted)" }} />}
        <FolderOpen size={14} style={{ color: "var(--c-accent)", flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-heading)" }}>
          {domain ?? "General"}
        </span>
        <span style={{ fontSize: 11, color: "var(--c-muted)", marginLeft: 4 }}>
          {controls.length} control{controls.length !== 1 ? "s" : ""} · {totalDocs} doc{totalDocs !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 12 }}>
          {controls.map(c => <TreeControlRow key={c.controlId} control={c} />)}
        </div>
      )}
    </div>
  );
}

// ── Framework block (collapsible) ─────────────────────────────────────────

function TreeFrameworkSection({ fw, defaultOpen = false }: { fw: EvidenceTreeFramework; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ ...card, marginBottom: 16, padding: 0, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "14px 18px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={16} style={{ color: "var(--c-muted)" }} /> : <ChevronRight size={16} style={{ color: "var(--c-muted)" }} />}
        <Layers size={16} style={{ color: "var(--c-accent)", flexShrink: 0 }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-heading)", flex: 1 }}>
          {fw.label}
        </span>
        <span style={{ ...countTag, fontSize: 11, padding: "0 6px", color: fw.totalDocs > 0 ? "var(--c-heading)" : "var(--c-muted)" }}>
          {fw.totalDocs} doc{fw.totalDocs !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 14px" }}>
          {fw.domains.map(d => (
            <TreeDomainSection key={d.domain ?? "__general"} domain={d.domain} controls={d.controls} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Export action bar ─────────────────────────────────────────────────────

function ExportActionBar({ orgName }: { orgName: string }) {
  const [state, setState] = useState<"idle" | "building" | "ready" | "error">("idle");
  const [result, setResult] = useState<ExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleExport() {
    setState("building");
    setResult(null);
    setError(null);
    try {
      const res = await api.post<ExportResult>("/export/evidence-package", {});
      setResult(res);
      setState("ready");
    } catch (e) {
      const msg = e instanceof ApiError
        ? (typeof e.payload === "object" && e.payload && "error" in e.payload
            ? String((e.payload as { error: unknown }).error)
            : e.message)
        : "Unexpected error. Please try again.";
      setError(msg);
      setState("error");
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "12px 16px",
        borderRadius: 3,
        background: "var(--c-accentBg)",
        border: "1.5px solid var(--c-accent)",
        marginBottom: 24,
      }}
    >
      <Package size={16} style={{ color: "var(--c-accent)", flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: "var(--c-text)", flex: 1 }}>
        Export all evidence as a structured ZIP archive — organised by standard, domain, and control.
      </span>
      {state === "idle" || state === "error" ? (
        <button
          onClick={handleExport}
          style={{
            padding: "8px 18px",
            borderRadius: 3,
            border: "none",
            background: "var(--c-accent)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <Download size={13} />
          Export Package
        </button>
      ) : state === "building" ? (
        <span style={{ fontSize: 12, color: "var(--c-muted)", flexShrink: 0 }}>Building package…</span>
      ) : null}
      {state === "ready" && result && (
        <a
          href={result.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "8px 18px",
            borderRadius: 3,
            background: "var(--c-accent)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <Download size={13} />
          Download ZIP ({fmtBytes(result.zipSizeBytes)})
        </a>
      )}
      {state === "error" && (
        <span style={{ fontSize: 12, color: "var(--c-danger)", flexShrink: 0 }}>{error}</span>
      )}
      {orgName && <span style={{ display: "none" }}>{orgName}</span>}
    </div>
  );
}

// ── Main evidence tree view ───────────────────────────────────────────────

function EvidenceTreeView({ tree }: { tree: EvidenceTree }) {
  const totalDocs = new Set([
    ...tree.frameworks.flatMap((fw) => fw.domains.flatMap((d) => d.controls.flatMap((c) => c.docs.map((doc) => doc.id)))),
    ...tree.legal.flatMap((l) => l.docs.map((doc) => doc.id)),
    ...tree.untagged.map((doc) => doc.id),
  ]).size;

  return (
    <div>
      <ExportActionBar orgName={tree.orgName} />

      {totalDocs === 0 && (
        <div style={{ ...emptyFrame, padding: 32, color: "var(--c-muted)", fontSize: 13 }}>
          No evidence documents found. Upload evidence on the Evidence Repository page and tag it to controls.
        </div>
      )}

      {/* Frameworks */}
      {tree.frameworks.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {tree.frameworks.map((fw, i) => (
            <TreeFrameworkSection key={fw.frameworkId} fw={fw} defaultOpen={i === 0} />
          ))}
        </div>
      )}

      {/* Legal obligations */}
      {tree.legal.length > 0 && (
        <div style={{ ...card, marginBottom: 16, padding: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 18px",
              borderBottom: "1px solid var(--c-border)",
            }}
          >
            <Scale size={16} style={{ color: "var(--c-accent)" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-heading)" }}>
              Legal Obligations
            </span>
          </div>
          <div style={{ padding: "10px 18px 14px" }}>
            {tree.legal.map(leg => (
              <TreeDomainSection
                key={leg.regulation}
                domain={leg.regulation}
                controls={[
                  {
                    controlId: "Legal",
                    controlName: leg.regulation,
                    kind: "CLAUSE",
                    docs: leg.docs,
                  },
                ]}
              />
            ))}
          </div>
        </div>
      )}

      {/* Untagged */}
      {tree.untagged.length > 0 && (
        <div style={{ ...card, marginBottom: 16, padding: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 18px",
              borderBottom: "1px solid var(--c-border)",
            }}
          >
            <FileText size={16} style={{ color: "var(--c-muted)" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-heading)" }}>
              Untagged Evidence
            </span>
            <span style={{ fontSize: 12, color: "var(--c-muted)" }}>
              {tree.untagged.length} file{tree.untagged.length !== 1 ? "s" : ""} not linked to a control
            </span>
          </div>
          <div style={{ padding: "10px 18px 14px" }}>
            {tree.untagged.map(d => <TreeDocRow key={d.id} doc={d} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function EvidenceExportPage() {
  const [tree, setTree] = useState<EvidenceTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTree = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get<EvidenceTree>("/export/evidence-tree")
      .then(setTree)
      .catch(() => setError("Failed to load evidence. Please refresh and try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  if (loading) return <LoadingBlock />;

  if (error) {
    return (
      <div style={{ padding: "32px 24px" }}>
        <div style={{ color: "var(--c-danger)", fontSize: 13, marginBottom: 12 }}>{error}</div>
        <button
          onClick={loadTree}
          style={{
            padding: "7px 16px",
            borderRadius: 3,
            border: "1px solid var(--c-border)",
            background: "var(--c-cardBg)",
            color: "var(--c-text)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "32px 24px", fontFamily: theme.font }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--c-heading)" }}>
          Audit pack
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--c-muted)", lineHeight: 1.5 }}>
          Browse all evidence linked to your enrolled standards and legal obligations.
          Select View to open a file in a new tab. Use the export button to download a structured ZIP archive.
        </p>
      </div>
      {tree && <EvidenceTreeView tree={tree} />}
    </div>
  );
}
